"use client"

import { DialogTitle } from "@radix-ui/react-dialog"
import Image from "next/image"
import { type ComponentProps, useEffect, useState } from "react"
import type { CarouselApi } from "@/components/ui/carousel"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { cn, getImageWithFallback } from "@/utilities"
import type { WooCommerceProduct } from "~/services/woo-commerce-service"

interface ProductDetailsSliderProps extends ComponentProps<"div"> {
  product: WooCommerceProduct
}

export function ProductDetailsSlider({
  product,
  className,
  ...props
}: ProductDetailsSliderProps) {
  const images = product.images || []

  const [modalApi, setModalApi] = useState<CarouselApi | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isVertical, setIsVertical] = useState(true)

  // Detect screen size to set carousel orientation
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)")

    const handleMediaChange = (e: MediaQueryListEvent) => {
      setIsVertical(e.matches)
    }

    setIsVertical(mediaQuery.matches)
    mediaQuery.addEventListener("change", handleMediaChange)

    return () => {
      mediaQuery.removeEventListener("change", handleMediaChange)
    }
  }, [])

  /* Sync modal carousel when opened */
  useEffect(() => {
    if (isModalOpen && modalApi) {
      modalApi.scrollTo(currentIndex, true)
    }
  }, [isModalOpen, modalApi, currentIndex])

  if (images.length === 0) {
    return (
      <div className='flex aspect-square items-center justify-center bg-gray-100'>
        <span className='text-gray-400'>Sem imagem disponível</span>
      </div>
    )
  }

  const currentImage = images[currentIndex]

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const isEnterKey = event.key === "Enter"
    const isSpaceKey = event.key === " "

    if (isEnterKey || isSpaceKey) {
      event.preventDefault()
      handleOpenModal()
    }
  }

  return (
    <>
      <div
        className={cn(
          "flex w-full flex-col gap-4 md:flex-row-reverse",
          className
        )}
        {...props}
      >
        <button
          className='group relative grid aspect-square w-full min-w-60 overflow-hidden bg-white'
          onClick={handleOpenModal}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          type='button'
        >
          <Image
            alt={currentImage?.alt || product.name}
            className='object-contain transition-transform duration-300 hover:scale-105'
            fill
            priority
            sizes='(max-width: 768px) 100vw, 50vw'
            src={getImageWithFallback(currentImage?.src || "")}
            unoptimized={currentImage?.src.includes("localhost")}
          />
          <div
            aria-label='Instruções para visualizar imagem ampliada'
            className='z-50 mb-4 self-end rounded-lg bg-black/25 px-4 py-2 backdrop-blur-sm'
            role='tooltip'
          >
            <span className='fade-in hidden animate-in text-white transition-opacity duration-300 group-hover:block'>
              Clique para ampliar a imagem
            </span>
            <span className='fade-in animate-in text-white transition-opacity duration-300 group-hover:hidden'>
              Mover sobre a imagem para ampliar
            </span>
          </div>
        </button>

        {images.length > 1 && (
          <Carousel
            className={cn(
              "group mx-auto w-full",
              isVertical && "max-h-90 max-w-30"
            )}
            opts={{
              align: "start",
              loop: false,
            }}
            orientation={isVertical ? "vertical" : "horizontal"}
          >
            <CarouselContent
              className={cn("w-full gap-3", isVertical && "max-h-90")}
            >
              {images.map((image, index) => {
                const isSelected = index === currentIndex
                const borderColor = isSelected
                  ? "border-primary"
                  : "border-gray-200 hover:border-primary/30"

                return (
                  <CarouselItem
                    className={cn(
                      "border p-0 md:shrink",
                      isVertical ? "basis-1/3" : "basis-1/4",
                      borderColor
                    )}
                    key={image.id}
                  >
                    <button
                      className='relative aspect-square w-full overflow-hidden transition-colors'
                      onClick={() => setCurrentIndex(index)}
                      type='button'
                    >
                      <Image
                        alt={
                          image.alt || `${product.name} - imagem ${index + 1}`
                        }
                        className='object-contain'
                        fill
                        sizes={isVertical ? "120px" : "100px"}
                        src={getImageWithFallback(image.src)}
                        unoptimized={image.src.includes("localhost")}
                      />
                    </button>
                  </CarouselItem>
                )
              })}
            </CarouselContent>

            <CarouselPrevious
              className='opacity-0 transition-opacity duration-300 group-hover:opacity-100'
              internal
            />
            <CarouselNext
              className='opacity-0 transition-opacity duration-300 group-hover:opacity-100'
              internal
            />
          </Carousel>
        )}
      </div>

      <Dialog onOpenChange={setIsModalOpen} open={isModalOpen}>
        <DialogContent className='max-w-4xl border-0 bg-black/95 p-0'>
          <DialogTitle className='sr-only'>Imagem do produto</DialogTitle>

          <Carousel
            className='w-full'
            opts={{ loop: true }}
            setApi={setModalApi}
          >
            <CarouselContent aria-describedby='Imagem do produto'>
              {images.map((image) => (
                <CarouselItem key={image.id}>
                  <div className='relative aspect-square bg-white'>
                    <Image
                      alt={image.alt || product.name}
                      className='object-contain'
                      fill
                      sizes='(max-width: 768px) 100vw, 768px'
                      src={getImageWithFallback(image.src)}
                      unoptimized={image.src.includes("localhost")}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

            <CarouselPrevious className='p-4 text-primary disabled:text-muted-foreground' />
            <CarouselNext className='p-4 text-primary disabled:text-muted-foreground' />
          </Carousel>
        </DialogContent>
      </Dialog>
    </>
  )
}
